/* Native PDP comparison and inspiration carousels. */
(() => {
  'use strict';
  const instances = new Map();
  const behavior = () => matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  function init(scope = document) {
    const pagers = [...(scope.matches?.('[data-card-pagination]') ? [scope] : []), ...scope.querySelectorAll('[data-card-pagination]')];
    pagers.forEach(pager => {
      if (instances.has(pager)) return;
      const track = pager.closest('.loop-wrap').querySelector('[data-card-track]');
      if (!track) return;
      const controller = new AbortController(), { signal } = controller;
      let positions = [], frame = 0;
      pager.closest('.loop-wrap').querySelectorAll('[data-card-prev], [data-card-next]').forEach(button => {
        button.addEventListener('click', event => {
          event.stopPropagation();
          const current = positions.reduce((best, left, i) => Math.abs(left-track.scrollLeft) < Math.abs(positions[best]-track.scrollLeft) ? i : best, 0);
          const next = Math.max(0, Math.min(positions.length-1, current + (button.hasAttribute('data-card-prev') ? -1 : 1)));
          if (positions.length) track.scrollTo({ left: positions[next], behavior: behavior() });
        }, { signal });
      });
      const active = () => {
        const index = positions.reduce((best, left, i) => Math.abs(left-track.scrollLeft) < Math.abs(positions[best]-track.scrollLeft) ? i : best, 0);
        [...pager.children].forEach((button, i) => button.setAttribute('aria-current', String(i === index)));
        const wrap = pager.closest('.loop-wrap');
        wrap.querySelectorAll('[data-card-prev]').forEach(button => { button.disabled = track.scrollLeft <= 1; });
        wrap.querySelectorAll('[data-card-next]').forEach(button => { button.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1; });
      };
      const measure = () => {
        const max = Math.max(0, track.scrollWidth-track.clientWidth), items = [...track.children], first = items[0]?.offsetLeft || 0;
        const next = [...new Set(items.map(item => Math.round(Math.max(0, Math.min(max, item.offsetLeft-first))*100)/100))];
        if (max > 1 && next.at(-1) !== Math.round(max*100)/100) next.push(Math.round(max*100)/100);
        if (JSON.stringify(next) !== JSON.stringify(positions)) {
          positions = next; pager.replaceChildren(...positions.map((left, i) => {
            const button = document.createElement('button'); button.type = 'button'; button.setAttribute('aria-label', (pager.dataset.slideLabel || 'Slide') + ' ' + (i+1));
            button.addEventListener('click', () => track.scrollTo({ left, behavior: behavior() }), { signal }); return button;
          }));
        }
        pager.hidden = positions.length < 2; active();
      };
      const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(measure); };
      track.addEventListener('scroll', active, { signal, passive: true });
      const observer = new ResizeObserver(schedule); observer.observe(track); [...track.children].forEach(item=>observer.observe(item));
      window.addEventListener('resize', schedule, { signal }); measure();
      instances.set(pager, () => { controller.abort(); observer.disconnect(); cancelAnimationFrame(frame); instances.delete(pager); });
    });
    const roots = [...(scope.matches?.('[data-pdp-social], [data-pdp-comparison], [data-pdp-inspiration], [data-pdp-faq], [data-campaign-carousel]') ? [scope] : []), ...scope.querySelectorAll('[data-pdp-social], [data-pdp-comparison], [data-pdp-inspiration], [data-pdp-faq], [data-campaign-carousel]')];
    roots.forEach(root => {
      if (instances.has(root)) return;
      const controller = new AbortController(), { signal } = controller;
      if (root.matches('[data-campaign-carousel]')) {
        let gesture = null;
        const campaignTrack = root.querySelector('.loop-slider__track');
        const updateCampaignControls = () => {
          const dots = [...root.querySelectorAll('[data-slider-dot]')];
          const current = dots.findIndex(dot => dot.getAttribute('aria-current') === 'true');
          const previous = root.querySelector('[data-slider-prev]'), next = root.querySelector('[data-slider-next]');
          if (previous) previous.disabled = current <= 0;
          if (next) next.disabled = current >= dots.length-1;
        };
        root.querySelectorAll('[data-slider-dot]').forEach(dot => dot.addEventListener('click', () => requestAnimationFrame(updateCampaignControls), { signal }));
        campaignTrack?.addEventListener('scroll', updateCampaignControls, { signal, passive: true });
        updateCampaignControls();
        root.addEventListener('pointerdown', event => {
          if (!event.isPrimary || event.button !== 0 || event.target.closest('a,button,input,select,textarea,video[controls]')) return;
          gesture = { id: event.pointerId, x: event.clientX, y: event.clientY };
        }, { signal, passive: true });
        root.addEventListener('pointerup', event => {
          if (!gesture || gesture.id !== event.pointerId) return;
          const dx = event.clientX-gesture.x, dy = event.clientY-gesture.y; gesture = null;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)*1.25) root.querySelector(dx < 0 ? '[data-slider-next]' : '[data-slider-prev]')?.click();
        }, { signal, passive: true });
        root.addEventListener('pointercancel', () => { gesture = null; }, { signal, passive: true });
      }
      if (root.matches('[data-pdp-social]')) {
        const track = root.querySelector('[data-social-track]'), cards = [...track.children], counter = root.querySelector('[data-social-current]');
        const index = () => cards.reduce((closest, card, i) => Math.abs(card.offsetLeft - cards[0].offsetLeft - track.scrollLeft) < Math.abs(cards[closest].offsetLeft - cards[0].offsetLeft - track.scrollLeft) ? i : closest, 0);
        const update = () => { if (counter) counter.textContent = String(index() + 1); };
        root.querySelectorAll('[data-social-prev], [data-social-next]').forEach(button => button.addEventListener('click', () => {
          const next = Math.max(0, Math.min(cards.length - 1, index() + (button.hasAttribute('data-social-prev') ? -1 : 1)));
          if (cards[next]) track.scrollTo({ left: cards[next].offsetLeft - cards[0].offsetLeft, behavior: behavior() });
        }, { signal }));
        track.addEventListener('scroll', update, { signal, passive: true });
        update();
      }
      if (root.matches('[data-pdp-inspiration]')) {
        const track = root.querySelector('[data-inspiration-track]');
        root.querySelectorAll('[data-inspiration-prev], [data-inspiration-next]').forEach(button => button.addEventListener('click', () => track.scrollBy({ left: 128 * (button.hasAttribute('data-inspiration-prev') ? -1 : 1), behavior: behavior() }), { signal }));
      }
      if (root.matches('[data-pdp-faq]')) {
        const button = root.querySelector('[data-pdp-faq-more]');
        button?.addEventListener('click', () => {
          const expanded = button.getAttribute('aria-expanded') !== 'true';
          root.querySelectorAll('[data-faq-additional]').forEach(item => { item.hidden = !expanded; if (!expanded) item.open = false; });
          button.setAttribute('aria-expanded', String(expanded));
          button.firstChild.textContent = expanded ? button.dataset.lessLabel : button.dataset.moreLabel;
        }, { signal });
      }
      if (root.matches('[data-pdp-comparison]')) {
        root.querySelectorAll('[data-pdp-compare-select]').forEach(select => select.addEventListener('change', () => {
          const template = [...root.querySelectorAll('template[data-pdp-compare-template]')].find(item => item.dataset.pdpCompareTemplate === select.value), column = [...root.querySelectorAll('[data-pdp-compare-column]')].find(item => item.dataset.pdpCompareColumn === select.dataset.pdpCompareSelect);
          if (!template || !column) return;
          column.replaceChildren(template.content.cloneNode(true));
          root.querySelector('[data-pdp-compare-status]').textContent = select.selectedOptions[0].textContent;
        }, { signal }));
      }
      instances.set(root, () => { controller.abort(); instances.delete(root); });
    });
  }
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => { for (const [root, destroy] of instances) if (event.target === root || event.target.contains(root)) destroy(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
})();
